import { useMemo, useState } from "react";
import { RotateCcw, RotateCw, ZoomIn, ZoomOut, Box } from "lucide-react";
import { useVanDesign } from "./VanDesignContext";
import { VanHeader, NCard, MonoBadge } from "./vanUI";
import { catalogOf } from "./vanData";

const TOP = { sleep: "#FFE08A", kitchen: "#A4DCFA", storage: "#C9F5D2", seat: "#FFC9C9", utility: "#E2D4F8", work: "#FFD9A6", bath: "#B9E4FF" };
const shade = (hex, f) => {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.round(((n >> 16) & 255) * f), g = Math.round(((n >> 8) & 255) * f), b = Math.round((n & 255) * f);
  return `rgb(${r},${g},${b})`;
};

const LAYERS = [
  { key: "finished", label: "Finished" },
  { key: "shell", label: "Cabinet Shell" },
  { key: "electrical", label: "Electrical" },
  { key: "plumbing", label: "Plumbing" },
];
const LAYER_CATS = { electrical: ["utility"], plumbing: ["bath", "utility"] };
const LAYER_TYPES = { electrical: ["electrical_cabinet"], plumbing: ["water_system", "shower_pod", "toilet"] };

function rotateRect(m, rot, W, L) {
  const { x, y, w, h } = m;
  if (rot === 1) return { x: y, y: W - x - w, w: h, h: w };
  if (rot === 2) return { x: W - x - w, y: L - y - h, w, h };
  if (rot === 3) return { x: L - y - h, y: x, w: h, h: w };
  return { x, y, w, h };
}

export default function VanDesign3D({ onBack }) {
  const { project } = useVanDesign();
  const [rot, setRot] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [layer, setLayer] = useState("finished");
  const interior = project.van?.interior || { length: 3200, width: 1750, height: 1850 };
  const modules = project.modules || [];

  const swapped = rot % 2 === 1;
  const W = swapped ? interior.length : interior.width;
  const L = swapped ? interior.width : interior.length;

  const view = useMemo(() => {
    const maxModH = Math.max(1000, ...modules.map((m) => catalogOf(m.type)?.heightMM || 400));
    const spanX = (W + L) * 0.87;
    const spanY = (W + L) * 0.5 + maxModH;
    const fit = Math.min(340 / spanX, 300 / spanY);
    const s = fit * zoom;
    const ox = 190 - ((W - L) * s * 0.87) / 2;   // centre horizontally
    const oy = 30 + maxModH * s;
    const iso = (X, Y, Z) => [ox + (X - Y) * s * 0.87, oy + (X + Y) * s * 0.5 - Z * s];
    const boxes = modules.map((m0) => {
      const m = rotateRect(m0, rot, interior.width, interior.length);
      const c = catalogOf(m0.type);
      return { m0, m, c, H: c?.heightMM || 400, near: m.x + m.y + m.w + m.h };
    }).sort((a, b) => a.near - b.near);
    return { s, iso, boxes };
  }, [modules, rot, zoom, interior, W, L]);

  const emphasize = (type, cat) => {
    if (layer === "finished" || layer === "shell") return true;
    return (LAYER_TYPES[layer] || []).includes(type) || (LAYER_CATS[layer] || []).includes(cat);
  };

  const { iso, boxes } = view;
  // floor plane corners
  const fp = [iso(0, 0, 0), iso(W, 0, 0), iso(W, L, 0), iso(0, L, 0)];

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-[#FDFBF7] max-w-[412px] mx-auto" data-testid="van-3d">
      <VanHeader title="3D Walkthrough" sub="Isometric preview" onBack={onBack} step="ISO" testid="van-3d-header" />
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-3 space-y-3">
        <NCard className="p-2.5 bg-[#FFF4D6] flex items-center gap-2">
          <Box size={14} /><p className="font-mono text-[9px] text-black/70">Generated from your live layout. Rotate, zoom and toggle layers. <MonoBadge>preview</MonoBadge></p>
        </NCard>

        <div className="bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_#000] overflow-hidden">
          <svg width="100%" viewBox="0 0 380 340" style={{ background: "#FDFBF7" }} data-testid="van-3d-canvas">
            {/* floor */}
            <polygon points={fp.map((p) => p.join(",")).join(" ")} fill="#EDE7DA" stroke="#000" strokeWidth="1.5" />
            {boxes.map(({ m0, m, c, H }) => {
              const cat = c?.cat || "storage";
              const on = emphasize(m0.type, cat);
              const base = TOP[cat] || "#DDD";
              const shell = layer === "shell";
              const opacity = on ? 1 : 0.12;
              const p = {
                a: iso(m.x, m.y, H), b: iso(m.x + m.w, m.y, H), cc: iso(m.x + m.w, m.y + m.h, H), d: iso(m.x, m.y + m.h, H),
                a0: iso(m.x, m.y, 0), b0: iso(m.x + m.w, m.y, 0), cc0: iso(m.x + m.w, m.y + m.h, 0), d0: iso(m.x, m.y + m.h, 0),
              };
              const face = (pts, fill) => <polygon points={pts.map((q) => q.join(",")).join(" ")} fill={shell ? "none" : fill} stroke="#000" strokeWidth="1" opacity={opacity} />;
              return (
                <g key={m0.id}>
                  {face([p.a0, p.b0, p.b, p.a], shade(base, 0.7))}   {/* left/front face */}
                  {face([p.b0, p.cc0, p.cc, p.b], shade(base, 0.55))} {/* right face */}
                  {face([p.a, p.b, p.cc, p.d], base)}                 {/* top */}
                  {on && !shell && (
                    <text x={(p.a[0] + p.cc[0]) / 2} y={(p.a[1] + p.cc[1]) / 2} textAnchor="middle" fontSize="6.5" fontFamily="monospace" fill="#000">
                      {c?.label?.split(" ")[0]}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* controls */}
        <div className="grid grid-cols-4 gap-2">
          <button onClick={() => setRot((r) => (r + 3) % 4)} data-testid="van-3d-rot-l" className="py-2.5 rounded-xl border-2 border-black bg-white grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><RotateCcw size={16} /></button>
          <button onClick={() => setRot((r) => (r + 1) % 4)} data-testid="van-3d-rot-r" className="py-2.5 rounded-xl border-2 border-black bg-white grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><RotateCw size={16} /></button>
          <button onClick={() => setZoom((z) => Math.min(2, z + 0.2))} data-testid="van-3d-zoom-in" className="py-2.5 rounded-xl border-2 border-black bg-white grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ZoomIn size={16} /></button>
          <button onClick={() => setZoom((z) => Math.max(0.6, z - 0.2))} data-testid="van-3d-zoom-out" className="py-2.5 rounded-xl border-2 border-black bg-white grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ZoomOut size={16} /></button>
        </div>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60 mb-1.5">Layers</p>
          <div className="grid grid-cols-2 gap-2">
            {LAYERS.map((l) => (
              <button key={l.key} onClick={() => setLayer(l.key)} data-testid={`van-3d-layer-${l.key}`}
                className={`font-mono text-[10px] font-black uppercase tracking-wide py-2 rounded-lg border-2 border-black ${layer === l.key ? "bg-black text-[#FDFBF7] shadow-[3px_3px_0px_#000]" : "bg-white text-black"}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
        {modules.length === 0 && <NCard className="p-3"><p className="font-mono text-[11px] text-black/60">No modules yet — add some in the editor to see them in 3D.</p></NCard>}
        <div className="h-2" />
      </div>
    </div>
  );
}
